import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { SECTORS, CATEGORIES } from "@/data/catalog";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";

// Native <select> looks better on mobile and reliably works inside Dialog.
function NativeSelect({ value, onChange, placeholder, options, testid }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="mt-1 flex h-11 w-full items-center rounded-md border border-slate-300 bg-white px-3 text-base text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 appearance-none bg-no-repeat"
      style={{
        backgroundImage: "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b' stroke-width='2'%3e%3cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7' /%3e%3c/svg%3e\")",
        backgroundPosition: "right 0.75rem center",
        backgroundSize: "1.25rem",
        paddingRight: "2.5rem",
      }}
    >
      <option value="" disabled>{placeholder}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

export default function GetQuoteModal({ open, onOpenChange, defaultCategory = "", defaultSector = "" }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    category: defaultCategory,
    subcategory: "",
    sector: defaultSector,
    urgency: "flexible",
    description: "",
    name: "",
    phone: "",
    email: "",
  });

  const cat = CATEGORIES.find((c) => c.key === form.category);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name || !form.phone || !form.description) {
      toast.error("Please fill your name, phone and describe the service");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/quotes", form);
      toast.success(`Sent to ${data.matched.length} matching business${data.matched.length === 1 ? "" : "es"}`);
      setDone(true);
    } catch (e) {
      toast.error("Failed to submit — try again");
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    onOpenChange(false);
    setTimeout(() => { setStep(1); setDone(false); }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-white w-[calc(100vw-1rem)] sm:w-full sm:max-w-lg max-h-[90vh] overflow-y-auto p-4 sm:p-6"
        data-testid="get-quote-modal"
      >
        {done ? (
          <div className="text-center py-6">
            <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-3" />
            <h3 className="text-xl font-bold text-slate-900">Quote request sent!</h3>
            <p className="text-slate-600 mt-2">The top 3 matching businesses will reach out to you on WhatsApp shortly.</p>
            <Button onClick={close} className="mt-6 bg-emerald-800 hover:bg-emerald-900" data-testid="quote-done-btn">Done</Button>
          </div>
        ) : (
          <>
            <DialogHeader className="text-left">
              <DialogTitle className="text-xl sm:text-2xl" style={{ fontFamily: "Outfit" }}>Get free quotes</DialogTitle>
              <DialogDescription className="text-sm">Tell us what you need — we'll send it to the top matching pros in your area.</DialogDescription>
            </DialogHeader>

            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <Label>Category</Label>
                  <NativeSelect
                    value={form.category}
                    onChange={(v) => setForm((f) => ({ ...f, category: v, subcategory: "" }))}
                    placeholder="Pick a category"
                    testid="quote-category-select"
                    options={CATEGORIES.map((c) => ({ value: c.key, label: c.name }))}
                  />
                </div>
                {cat && (
                  <div>
                    <Label>Service type</Label>
                    <NativeSelect
                      value={form.subcategory}
                      onChange={(v) => set("subcategory", v)}
                      placeholder="Any specific service?"
                      testid="quote-subcategory-select"
                      options={cat.sub.map((s) => ({ value: s.key, label: s.name }))}
                    />
                  </div>
                )}
                <div>
                  <Label>Sector</Label>
                  <NativeSelect
                    value={form.sector}
                    onChange={(v) => set("sector", v)}
                    placeholder="Your sector"
                    testid="quote-sector-select"
                    options={SECTORS.map((s) => ({ value: s, label: s }))}
                  />
                </div>
                <div>
                  <Label>Urgency</Label>
                  <NativeSelect
                    value={form.urgency}
                    onChange={(v) => set("urgency", v)}
                    placeholder="How urgent?"
                    testid="quote-urgency-select"
                    options={[
                      { value: "today", label: "Today / Emergency" },
                      { value: "this_week", label: "This week" },
                      { value: "flexible", label: "Flexible" },
                    ]}
                  />
                </div>
                <DialogFooter className="pt-2">
                  <Button onClick={() => setStep(2)} disabled={!form.category || !form.sector} className="bg-emerald-800 hover:bg-emerald-900 w-full h-11" data-testid="quote-next-btn">Next</Button>
                </DialogFooter>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <div>
                  <Label>Describe what you need</Label>
                  <Textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="e.g. AC not cooling in bedroom, need service tomorrow morning" className="mt-1" data-testid="quote-description-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Your name</Label>
                    <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1 h-11" data-testid="quote-name-input" />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input inputMode="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="03xx-xxxxxxx" className="mt-1 h-11" data-testid="quote-phone-input" />
                  </div>
                </div>
                <div>
                  <Label>Email (optional)</Label>
                  <Input type="email" inputMode="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1 h-11" data-testid="quote-email-input" />
                </div>
                <DialogFooter className="gap-2 flex-row justify-between sm:justify-end pt-2">
                  <Button variant="outline" onClick={() => setStep(1)} className="h-11 flex-1 sm:flex-none" data-testid="quote-back-btn">Back</Button>
                  <Button onClick={submit} disabled={loading} className="bg-emerald-800 hover:bg-emerald-900 h-11 flex-1 sm:flex-none" data-testid="quote-submit-btn">
                    {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Send request
                  </Button>
                </DialogFooter>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
